import React from "react";
import { Calendar, MapPin, Clock } from "lucide-react";
import { useEvents } from "../hooks/useEvents";
import { urlFor } from "../lib/sanity";

const MONTHS_ES = [
  "ENE", "FEB", "MAR", "ABR", "MAY", "JUN",
  "JUL", "AGO", "SEP", "OCT", "NOV", "DIC",
];

const DAYS_ES = ["DOM", "LUN", "MAR", "MIE", "JUE", "VIE", "SAB"];

const formatDate = (iso) => {
  if (!iso) return { day: "", line: "" };
  const [year, month, day] = String(iso).split("-").map(Number);
  if (!year || !month || !day) return { day: "", line: "" };
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return { day: "", line: "" };
  return {
    day: DAYS_ES[d.getDay()],
    line: `${String(d.getDate()).padStart(2, "0")}.${MONTHS_ES[d.getMonth()]}`,
  };
};

const Events = () => {
  const { events, loading } = useEvents();

  if (loading) return null;
  if (!events || events.length === 0) return null;

  return (
    <section id="events" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-600/30 to-transparent" />
        <div className="absolute top-1/4 left-0 w-80 h-80 bg-teal-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-12">
          <Calendar className="w-5 h-5 text-teal-400" />
          <span className="font-nunito text-white text-sm tracking-[0.25em] uppercase font-bold">
            Próximos Eventos
          </span>
          <div className="flex-1 h-px bg-teal-500/20" />
        </div>

        <div className="mb-12">
          <h2 className="font-bebas text-5xl md:text-6xl tracking-wide text-black lg:text-teal-400">
            Calendario <span className="text-teal-400">de Eventos</span>
          </h2>
          <p className="font-nunito text-slate-300 mt-2">
            No te pierdas las próximas presentaciones y proyectos.
          </p>
        </div>

        <div className="space-y-3">
          {events.map((ev, idx) => {
            const { day, line } = formatDate(ev.date);
            const isAlt = idx % 2 === 1;
            const rowBg = isAlt
              ? "bg-amber-500/15 hover:bg-amber-500/25"
              : "bg-white/5 hover:bg-white/10";

            return (
              <div
                key={ev._id}
                className={`group flex items-stretch gap-4 rounded-xl overflow-hidden backdrop-blur-md border border-white/5 transition-colors duration-300 ${rowBg}`}
              >
                <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 overflow-hidden">
                  {ev.image ? (
                    <img
                      src={urlFor(ev.image).width(200).height(200).fit("crop").url()}
                      alt={ev.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-teal-500/20 flex items-center justify-center">
                      <Calendar className="w-8 h-8 text-teal-400" />
                    </div>
                  )}
                </div>

                <div className="flex-1 flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 pr-4">
                  <div className="min-w-0">
                    <h3 className="font-bebas text-2xl sm:text-3xl text-black tracking-wide truncate">
                      {ev.title}
                    </h3>
                    {ev.description && (
                      <p className="font-nunito text-xs sm:text-sm text-slate-300 mt-0.5 line-clamp-2">
                        {ev.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                      {ev.lugar && (
                        <span className="inline-flex items-center gap-1 font-nunito text-xs text-slate-300">
                          <MapPin className="w-3.5 h-3.5 text-teal-400" />
                          {ev.lugar}
                        </span>
                      )}
                      {ev.hora && (
                        <span className="inline-flex items-center gap-1 font-nunito text-xs text-slate-300">
                          <Clock className="w-3.5 h-3.5 text-teal-400" />
                          {ev.hora}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-nunito text-[0.7rem] tracking-[0.25em] text-amber-400 font-bold">
                      {day}
                    </div>
                    <div className="font-bebas text-2xl sm:text-3xl text-black tracking-wider">
                      {line}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Events;
